/**
 * Sentry Error Monitoring Configuration
 *
 * Tracks ALL errors and performance across dev/prod environments
 *
 * @module SentryConfig
 * @version 3.5
 */

import * as Sentry from '@sentry/react'

// Sentry DSN - same for all environments
const SENTRY_DSN = 'https://4466ed8c14a827b857ee17c865260337@o4510398409670656.ingest.us.sentry.io/4510599662665728'

/**
 * Initialize Sentry - ENABLED FOR ALL ENVIRONMENTS
 */
export function initSentry() {
  const environment = import.meta.env.MODE || 'development'
  const isDev = import.meta.env.DEV

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,

    // Performance Monitoring - higher rate in dev
    tracesSampleRate: isDev ? 0.5 : 0.1,

    // Session Replay - 100% on errors, 10% normally
    replaysSessionSampleRate: isDev ? 0.3 : 0.1,
    replaysOnErrorSampleRate: 1.0, // Always capture replay on error

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration({
        // Track navigation and page load
        enableInp: true,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        // Mask sensitive inputs
        maskAllInputs: true,
      }),
      Sentry.httpClientIntegration({
        // Capture failed HTTP requests
        failedRequestStatusCodes: [[400, 599]],
      }),
    ],

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || '3.5.0',

    // Capture 100% of errors (no sampling for errors)
    sampleRate: 1.0,

    // Attach stack traces
    attachStacktrace: true,

    // Error filtering - minimal, we want to catch most errors
    beforeSend(event, hint) {
      const error = hint.originalException as Error

      // Don't send chunk loading errors (user refreshed during deploy)
      if (error?.message?.includes('Loading chunk') ||
          error?.message?.includes('Loading CSS chunk')) {
        return null
      }

      // Don't send ResizeObserver errors (browser quirk, not actionable)
      if (error?.message?.includes('ResizeObserver loop')) {
        return null
      }

      // Don't send Phantom API errors (external API, not actionable)
      if (error?.message?.includes('Method not found') ||
          error?.message?.includes('Phantom method not available') ||
          error?.message?.includes('window.phantom is undefined') ||
          error?.message?.includes('phantom.solana is undefined')) {
        return null
      }

      // Don't send wallet adapter errors on mobile (external API issues)
      if (error?.name?.includes('Wallet') &&
          (error?.message?.includes('not ready') || error?.message?.includes('load'))) {
        return null
      }

      return event
    },

    // Minimal ignore list
    ignoreErrors: [
      // Browser extensions causing issues
      'top.GLOBALS',
      // User canceled wallet connection (expected behavior)
      'User rejected the request',
      'User denied',
      // Phantom API errors (external API, not actionable)
      'Method not found',
      'Phantom method not available',
      'window.phantom is undefined',
      'phantom.solana is undefined',
      // Wallet adapter errors on mobile
      'WalletNotReadyError',
      'WalletLoadError',
      'WalletConnectionError',
    ],

    // Track errors from our domains
    allowUrls: [
      /takarafi\.com/,
      /takarafinance\.com/,
      /localhost/,
    ],

    // Add custom tags
    initialScope: {
      tags: {
        component: 'frontend',
      },
    },
  })

  console.log(`[Sentry] Initialized for ${environment} environment`)
}

// Export Sentry for manual error capturing
export { Sentry }

/**
 * Capture exception with context
 */
export function captureException(error: Error, context?: Record<string, unknown>): string {
  if (context) {
    Sentry.setContext('additional', context)
  }
  return Sentry.captureException(error)
}

/**
 * Capture API error with request details
 */
export function captureApiError(
  error: Error,
  endpoint: string,
  method: string,
  statusCode?: number,
  responseData?: unknown
): string {
  Sentry.setContext('api_request', {
    endpoint,
    method,
    statusCode,
    responseData: responseData ? JSON.stringify(responseData).substring(0, 500) : undefined,
  })

  Sentry.setTag('api_endpoint', endpoint)
  Sentry.setTag('api_method', method)
  if (statusCode) {
    Sentry.setTag('status_code', statusCode.toString())
  }

  return Sentry.captureException(error)
}

/**
 * Capture message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): string {
  return Sentry.captureMessage(message, level)
}

/**
 * Set user context - call after authentication
 */
export function setUser(user: {
  id: string
  email?: string
  username?: string
  walletAddress?: string
} | null) {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
      walletAddress: user.walletAddress,
    })
  } else {
    Sentry.setUser(null)
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info'
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level,
    timestamp: Date.now() / 1000,
  })
}

/**
 * Set custom tag
 */
export function setTag(key: string, value: string) {
  Sentry.setTag(key, value)
}

/**
 * Start a transaction for performance monitoring
 */
export function startTransaction(name: string, op: string) {
  return Sentry.startInactiveSpan({ name, op })
}
